import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

const SEEN_KEY = "redo.integrations-moved-notice-dismissed";

/** Query param the legacy per-product nav entries attach when sending users
 * here; dismissal strips it from the URL. */
export const MOVED_FROM_PARAM = "movedFrom";

/** Source surfaces that link here, used to tailor the notice copy. */
const SOURCE_LABELS: Record<string, string> = {
  marketing: "Marketing integrations",
  oms: "OMS integrations",
  chargebacks: "Chargeback integrations",
  "customer-accounts": "Customer account integrations",
  "order-tracking": "Order tracking integrations",
  returns: "Returns integrations",
};

function readSeen(): boolean {
  try {
    return localStorage.getItem(SEEN_KEY) === "true";
  } catch {
    return false;
  }
}

/** A moved-from arrival always shows (dismissal clears the param, so a present
 * param means a fresh arrival); direct navigation shows once, then never. */
export function shouldShowMovedNotice(args: {
  dismissed: boolean;
  seen: boolean;
  fromRedirect: boolean;
}): boolean {
  if (args.fromRedirect) {
    return true;
  }
  return !args.dismissed && !args.seen;
}

/** Notice that the scattered integration pages moved here — every time from a
 * legacy per-product entry point, once for direct navigation. */
export const IntegrationsMovedNotice = memo(function IntegrationsMovedNotice() {
  const [searchParams, setSearchParams] = useSearchParams();
  const movedFrom = searchParams.get(MOVED_FROM_PARAM) ?? undefined;

  const initiallySeen = useMemo(() => readSeen(), []);
  const [dismissed, setDismissed] = useState(false);

  const open = shouldShowMovedNotice({
    dismissed,
    fromRedirect: movedFrom !== undefined,
    seen: initiallySeen,
  });

  const close = useCallback(() => {
    setDismissed(true);
    try {
      localStorage.setItem(SEEN_KEY, "true");
    } catch {
      // localStorage unavailable; nothing to persist.
    }
    if (searchParams.has(MOVED_FROM_PARAM)) {
      const next = new URLSearchParams(searchParams);
      next.delete(MOVED_FROM_PARAM);
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  if (!open) {
    return null;
  }

  const sourceLabel = movedFrom ? SOURCE_LABELS[movedFrom] : undefined;

  return (
    <RedoModal
      onModalCloseRequested={close}
      primaryButton={{ onClick: close, text: "Got it" }}
      subtitle={
        sourceLabel
          ? `${sourceLabel} now live here, alongside every other integration.`
          : "Every integration now lives on one page."
      }
      title="Integrations have moved"
    >
      <Flex dir="column" gap="md">
        <Text fontSize="sm" textColor="secondary">
          We combined the returns, marketing, OMS, and chargeback integration
          pages into this single Integrations page. Browse everything in one
          place, filter by type, and connect or manage each integration from its
          own card.
        </Text>
        <Text fontSize="sm" textColor="secondary">
          Your existing connections are unchanged — nothing needs to be
          reconnected.
        </Text>
      </Flex>
    </RedoModal>
  );
});
